/**
 * Retention nudge logic — a long chat is a retention signal, so once a FREE user's chat
 * passes a message threshold we softly invite them to upgrade (more AI quota / storage /
 * smarter models). Pure decision + per-chat dismissal persistence so the UI stays dumb and
 * the rule is unit-tested. NO hard gate — purely a dismissible suggestion.
 */
export const RETENTION_THRESHOLD = 12;

const DISMISS_KEY = 'wisetree_retention_nudges_dismissed';

export interface NudgeInput {
  planTier: string;       // 'FREE' | 'PRO' | ... (case-insensitive)
  messageCount: number;
  dismissed: boolean;
}

/** Show only to FREE users, past the threshold, when not already dismissed for this chat. */
export function shouldShowRetentionNudge({ planTier, messageCount, dismissed }: NudgeInput): boolean {
  if (dismissed) return false;
  if ((planTier || '').toUpperCase() !== 'FREE') return false;
  return messageCount >= RETENTION_THRESHOLD;
}

/** The set of chat ids the user has dismissed the nudge for (localStorage-backed). */
export function getDismissedNudges(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

/** Remember that the user dismissed the nudge for a chat (idempotent, capped). */
export function markNudgeDismissed(chatId: string): void {
  try {
    const set = getDismissedNudges();
    set.add(chatId);
    // Keep the most recent 200 so the key can't grow unbounded.
    // (Array.from, not [...set] — the latter needs downlevelIteration at our es5 target.)
    const list = Array.from(set).slice(-200);
    localStorage.setItem(DISMISS_KEY, JSON.stringify(list));
  } catch {
    /* ignore — dismissal is best-effort */
  }
}

export function isNudgeDismissed(chatId: string): boolean {
  return getDismissedNudges().has(chatId);
}
